import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Palette, Layout, Sparkles, RotateCcw, Type, Image, Plus, Trash2, FileText, Wand2, Loader2 } from 'lucide-react';
import { PresentationSlide, ThemePreset, SlideAnimation, SlideTransition } from '../../types';
import { SLIDE_LAYOUTS, SLIDE_TRANSITIONS, ELEMENT_ANIMATIONS } from '../../data/themePresets';
import { useAuthStore } from '../../store/auth';
import ColorPicker from './ColorPicker';

const API_URL = import.meta.env.VITE_API_URL || '/api';

interface SlideSettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  slide: PresentationSlide;
  slideIndex: number;
  globalTheme: ThemePreset;
  onUpdate: (slideIndex: number, updates: Partial<PresentationSlide>) => void;
  isDark: boolean;
  presentationId?: string;
  onRefreshCredits?: () => void;
}

export default function SlideSettingsPanel({
  isOpen,
  onClose,
  slide,
  slideIndex,
  globalTheme,
  onUpdate,
  isDark,
  presentationId,
  onRefreshCredits,
}: SlideSettingsPanelProps) {
  const { token } = useAuthStore();

  // Local state for content editing
  const [activeTab, setActiveTab] = useState<'design' | 'content'>('design');
  const [localTitle, setLocalTitle] = useState(slide.title);
  const [localSubtitle, setLocalSubtitle] = useState(slide.subtitle || '');
  const [localContent, setLocalContent] = useState<string[]>(slide.content || []);
  const [localImage, setLocalImage] = useState(slide.image || '');
  const [localImageAlt, setLocalImageAlt] = useState(slide.imageAlt || '');
  const [localNotes, setLocalNotes] = useState(slide.notes || '');

  // AI generation state
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [isGeneratingContent, setIsGeneratingContent] = useState(false);
  const [imagePrompt, setImagePrompt] = useState('');
  const [contentInstructions, setContentInstructions] = useState('');
  const [showImagePrompt, setShowImagePrompt] = useState(false);
  const [showContentInstructions, setShowContentInstructions] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);

  // Sync local state when slide changes
  useEffect(() => {
    setLocalTitle(slide.title);
    setLocalSubtitle(slide.subtitle || '');
    setLocalContent(slide.content || []);
    setLocalImage(slide.image || '');
    setLocalImageAlt(slide.imageAlt || '');
    setLocalNotes(slide.notes || '');
  }, [slide]);

  // Generate image with Vertex AI
  const handleGenerateImage = async () => {
    if (!presentationId || !token) return;

    setIsGeneratingImage(true);
    setGenerationError(null);

    try {
      const response = await fetch(`${API_URL}/presentations/${presentationId}/slide/${slideIndex}/generate-image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ prompt: imagePrompt || undefined }),
      });

      const data = await response.json() as {
        success: boolean;
        error?: string;
        data?: { imageUrl: string; slide: PresentationSlide };
      };

      if (!data.success) {
        setGenerationError(data.error || 'Failed to generate image');
        return;
      }

      if (data.data?.imageUrl) {
        setLocalImage(data.data.imageUrl);
        onUpdate(slideIndex, { image: data.data.imageUrl, imageAlt: slide.title });
      }

      setShowImagePrompt(false);
      setImagePrompt('');
      onRefreshCredits?.();
    } catch (err) {
      setGenerationError(err instanceof Error ? err.message : 'Failed to generate image');
    } finally {
      setIsGeneratingImage(false);
    }
  };

  // Generate content with AI
  const handleGenerateContent = async () => {
    if (!presentationId || !token) return;

    setIsGeneratingContent(true);
    setGenerationError(null);

    try {
      const response = await fetch(`${API_URL}/presentations/${presentationId}/slide/${slideIndex}/generate-content`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          topic: localTitle || undefined,
          instructions: contentInstructions || undefined,
        }),
      });

      const data = await response.json() as {
        success: boolean;
        error?: string;
        data?: { slide: PresentationSlide };
      };

      if (!data.success) {
        setGenerationError(data.error || 'Failed to generate content');
        return;
      }

      if (data.data?.slide) {
        const newSlide = data.data.slide;
        setLocalTitle(newSlide.title);
        setLocalSubtitle(newSlide.subtitle || '');
        setLocalContent(newSlide.content || []);
        onUpdate(slideIndex, {
          title: newSlide.title,
          subtitle: newSlide.subtitle,
          content: newSlide.content,
        });
      }

      setShowContentInstructions(false);
      setContentInstructions('');
      onRefreshCredits?.();
    } catch (err) {
      setGenerationError(err instanceof Error ? err.message : 'Failed to generate content');
    } finally {
      setIsGeneratingContent(false);
    }
  };

  const handleBackgroundChange = (color: string) => {
    onUpdate(slideIndex, { backgroundColor: color });
  };

  const handleLayoutChange = (layout: PresentationSlide['layout']) => {
    onUpdate(slideIndex, { layout });
  };

  const handleTransitionChange = (transition: SlideTransition | '') => {
    if (transition === '') {
      // Reset to global - use undefined
      onUpdate(slideIndex, { transition: undefined });
    } else {
      onUpdate(slideIndex, { transition });
    }
  };

  const handleAnimationChange = (animation: SlideAnimation | '') => {
    if (animation === '') {
      // Reset to global - use undefined
      onUpdate(slideIndex, { animation: undefined });
    } else {
      onUpdate(slideIndex, { animation });
    }
  };

  const handleResetToGlobal = () => {
    onUpdate(slideIndex, {
      backgroundColor: undefined,
      layout: undefined,
      transition: undefined,
      animation: undefined,
    });
  };

  // Content editing handlers
  const handleTitleChange = (value: string) => {
    setLocalTitle(value);
    onUpdate(slideIndex, { title: value });
  };

  const handleSubtitleChange = (value: string) => {
    setLocalSubtitle(value);
    onUpdate(slideIndex, { subtitle: value || undefined });
  };

  const handleContentItemChange = (index: number, value: string) => {
    const newContent = [...localContent];
    newContent[index] = value;
    setLocalContent(newContent);
    onUpdate(slideIndex, { content: newContent });
  };

  const handleAddContentItem = () => {
    const newContent = [...localContent, ''];
    setLocalContent(newContent);
    onUpdate(slideIndex, { content: newContent });
  };

  const handleRemoveContentItem = (index: number) => {
    const newContent = localContent.filter((_, i) => i !== index);
    setLocalContent(newContent);
    onUpdate(slideIndex, { content: newContent });
  };

  const handleImageChange = (value: string) => {
    setLocalImage(value);
    onUpdate(slideIndex, { image: value || undefined });
  };

  const handleImageAltChange = (value: string) => {
    setLocalImageAlt(value);
    onUpdate(slideIndex, { imageAlt: value || undefined });
  };

  const handleNotesChange = (value: string) => {
    setLocalNotes(value);
    onUpdate(slideIndex, { notes: value || undefined });
  };

  const hasCustomSettings = slide.backgroundColor || slide.layout || slide.transition || slide.animation;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/30 z-40"
            onClick={onClose}
          />

          {/* Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className={`fixed right-0 top-0 bottom-0 w-96 z-50 shadow-2xl flex flex-col ${
              isDark ? 'bg-dark-800' : 'bg-white'
            }`}
          >
            {/* Header */}
            <div className={`h-16 border-b flex items-center justify-between px-4 flex-shrink-0 ${
              isDark ? 'border-dark-700' : 'border-slate-200'
            }`}>
              <div>
                <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Slide {slideIndex + 1} Settings
                </h3>
                <p className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                  Customize this slide
                </p>
              </div>
              <button
                onClick={onClose}
                className={`p-2 rounded-lg transition-colors ${
                  isDark ? 'hover:bg-dark-700 text-text-secondary' : 'hover:bg-slate-100 text-slate-500'
                }`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Tabs */}
            <div className={`flex border-b flex-shrink-0 ${isDark ? 'border-dark-700' : 'border-slate-200'}`}>
              <button
                onClick={() => setActiveTab('content')}
                className={`flex-1 py-3 px-4 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                  activeTab === 'content'
                    ? 'text-purple-500 border-b-2 border-purple-500'
                    : isDark ? 'text-text-secondary hover:text-white' : 'text-slate-500 hover:text-slate-900'
                }`}
              >
                <Type className="w-4 h-4" />
                Content
              </button>
              <button
                onClick={() => setActiveTab('design')}
                className={`flex-1 py-3 px-4 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                  activeTab === 'design'
                    ? 'text-purple-500 border-b-2 border-purple-500'
                    : isDark ? 'text-text-secondary hover:text-white' : 'text-slate-500 hover:text-slate-900'
                }`}
              >
                <Palette className="w-4 h-4" />
                Design
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-6">
              {/* Error Message */}
              {generationError && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-500 text-sm">
                  {generationError}
                </div>
              )}

              {activeTab === 'content' ? (
                <>
                  {/* AI Generate Content Button */}
                  {presentationId && (
                    <div className={`p-3 rounded-xl ${isDark ? 'bg-purple-500/10 border border-purple-500/20' : 'bg-purple-50 border border-purple-200'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <Wand2 className="w-4 h-4 text-purple-500" />
                        <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                          Generate with AI
                        </span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-500">
                          1 credit
                        </span>
                      </div>
                      {showContentInstructions ? (
                        <div className="space-y-2">
                          <textarea
                            value={contentInstructions}
                            onChange={(e) => setContentInstructions(e.target.value)}
                            placeholder="Optional: Add specific instructions for the AI (e.g., 'Focus on benefits', 'Include statistics')..."
                            rows={2}
                            className={`w-full px-3 py-2 rounded-lg border text-sm resize-none ${
                              isDark
                                ? 'bg-dark-700 border-dark-600 text-white placeholder:text-text-muted'
                                : 'bg-white border-slate-200 text-slate-900 placeholder:text-slate-400'
                            }`}
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={handleGenerateContent}
                              disabled={isGeneratingContent}
                              className="flex-1 py-2 px-3 bg-gradient-to-r from-purple-500 to-cyan-500 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                            >
                              {isGeneratingContent ? (
                                <>
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  Generating...
                                </>
                              ) : (
                                <>
                                  <Wand2 className="w-4 h-4" />
                                  Generate
                                </>
                              )}
                            </button>
                            <button
                              onClick={() => setShowContentInstructions(false)}
                              className={`py-2 px-3 rounded-lg text-sm ${
                                isDark ? 'bg-dark-700 text-text-secondary' : 'bg-slate-100 text-slate-600'
                              }`}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => setShowContentInstructions(true)}
                          className="w-full py-2 px-3 bg-purple-500/20 hover:bg-purple-500/30 text-purple-500 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors"
                        >
                          <Wand2 className="w-4 h-4" />
                          Generate Content
                        </button>
                      )}
                    </div>
                  )}

                  {/* Title */}
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      Title
                    </label>
                    <input
                      type="text"
                      value={localTitle}
                      onChange={(e) => handleTitleChange(e.target.value)}
                      placeholder="Slide title..."
                      className={`w-full px-3 py-2 rounded-lg border text-sm ${
                        isDark
                          ? 'bg-dark-700 border-dark-600 text-white placeholder:text-text-muted'
                          : 'bg-white border-slate-200 text-slate-900 placeholder:text-slate-400'
                      }`}
                    />
                  </div>

                  {/* Subtitle */}
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      Subtitle (Optional)
                    </label>
                    <input
                      type="text"
                      value={localSubtitle}
                      onChange={(e) => handleSubtitleChange(e.target.value)}
                      placeholder="Slide subtitle..."
                      className={`w-full px-3 py-2 rounded-lg border text-sm ${
                        isDark
                          ? 'bg-dark-700 border-dark-600 text-white placeholder:text-text-muted'
                          : 'bg-white border-slate-200 text-slate-900 placeholder:text-slate-400'
                      }`}
                    />
                  </div>

                  {/* Content Items (Bullet Points) */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        Content Points
                      </label>
                      <button
                        onClick={handleAddContentItem}
                        className={`p-1.5 rounded-lg transition-colors ${
                          isDark ? 'hover:bg-dark-700 text-purple-400' : 'hover:bg-slate-100 text-purple-600'
                        }`}
                        title="Add content point"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="space-y-2">
                      {localContent.map((item, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <span className={`text-xs font-medium w-5 ${isDark ? 'text-text-muted' : 'text-slate-400'}`}>
                            {index + 1}.
                          </span>
                          <input
                            type="text"
                            value={item}
                            onChange={(e) => handleContentItemChange(index, e.target.value)}
                            placeholder={`Point ${index + 1}...`}
                            className={`flex-1 px-3 py-2 rounded-lg border text-sm ${
                              isDark
                                ? 'bg-dark-700 border-dark-600 text-white placeholder:text-text-muted'
                                : 'bg-white border-slate-200 text-slate-900 placeholder:text-slate-400'
                            }`}
                          />
                          {localContent.length > 1 && (
                            <button
                              onClick={() => handleRemoveContentItem(index)}
                              className={`p-1.5 rounded-lg transition-colors ${
                                isDark ? 'hover:bg-red-500/20 text-red-400' : 'hover:bg-red-50 text-red-500'
                              }`}
                              title="Remove point"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Image Section */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Image className={`w-4 h-4 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                      <label className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        Slide Image
                      </label>
                    </div>

                    {/* AI Generate Image Button */}
                    {presentationId && (
                      <div className={`p-3 rounded-xl mb-3 ${isDark ? 'bg-cyan-500/10 border border-cyan-500/20' : 'bg-cyan-50 border border-cyan-200'}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <Wand2 className="w-4 h-4 text-cyan-500" />
                          <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                            Generate Image with AI
                          </span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-500">
                            2 credits
                          </span>
                        </div>
                        {showImagePrompt ? (
                          <div className="space-y-2">
                            <textarea
                              value={imagePrompt}
                              onChange={(e) => setImagePrompt(e.target.value)}
                              placeholder={`Optional: Describe the image you want (leave empty to auto-generate based on slide content: "${localTitle}")...`}
                              rows={2}
                              className={`w-full px-3 py-2 rounded-lg border text-sm resize-none ${
                                isDark
                                  ? 'bg-dark-700 border-dark-600 text-white placeholder:text-text-muted'
                                  : 'bg-white border-slate-200 text-slate-900 placeholder:text-slate-400'
                              }`}
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={handleGenerateImage}
                                disabled={isGeneratingImage}
                                className="flex-1 py-2 px-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                              >
                                {isGeneratingImage ? (
                                  <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Generating...
                                  </>
                                ) : (
                                  <>
                                    <Wand2 className="w-4 h-4" />
                                    Generate
                                  </>
                                )}
                              </button>
                              <button
                                onClick={() => setShowImagePrompt(false)}
                                className={`py-2 px-3 rounded-lg text-sm ${
                                  isDark ? 'bg-dark-700 text-text-secondary' : 'bg-slate-100 text-slate-600'
                                }`}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => setShowImagePrompt(true)}
                            className="w-full py-2 px-3 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-500 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors"
                          >
                            <Wand2 className="w-4 h-4" />
                            Generate Image
                          </button>
                        )}
                      </div>
                    )}

                    {/* Manual Image URL */}
                    <p className={`text-xs mb-2 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                      Or enter an image URL manually:
                    </p>
                    <input
                      type="url"
                      value={localImage}
                      onChange={(e) => handleImageChange(e.target.value)}
                      placeholder="https://example.com/image.jpg"
                      className={`w-full px-3 py-2 rounded-lg border text-sm ${
                        isDark
                          ? 'bg-dark-700 border-dark-600 text-white placeholder:text-text-muted'
                          : 'bg-white border-slate-200 text-slate-900 placeholder:text-slate-400'
                      }`}
                    />
                    {localImage && (
                      <div className="mt-2">
                        <img
                          src={localImage}
                          alt={localImageAlt || 'Slide image preview'}
                          className="w-full h-32 object-cover rounded-lg"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                        <input
                          type="text"
                          value={localImageAlt}
                          onChange={(e) => handleImageAltChange(e.target.value)}
                          placeholder="Image alt text (for accessibility)"
                          className={`w-full mt-2 px-3 py-2 rounded-lg border text-sm ${
                            isDark
                              ? 'bg-dark-700 border-dark-600 text-white placeholder:text-text-muted'
                              : 'bg-white border-slate-200 text-slate-900 placeholder:text-slate-400'
                          }`}
                        />
                      </div>
                    )}
                  </div>

                  {/* Presenter Notes */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className={`w-4 h-4 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                      <label className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        Presenter Notes
                      </label>
                    </div>
                    <textarea
                      value={localNotes}
                      onChange={(e) => handleNotesChange(e.target.value)}
                      placeholder="Add notes for this slide..."
                      rows={4}
                      className={`w-full px-3 py-2 rounded-lg border text-sm resize-none ${
                        isDark
                          ? 'bg-dark-700 border-dark-600 text-white placeholder:text-text-muted'
                          : 'bg-white border-slate-200 text-slate-900 placeholder:text-slate-400'
                      }`}
                    />
                  </div>
                </>
              ) : (
                <>
                  {/* Reset Button */}
                  {hasCustomSettings && (
                    <button
                      onClick={handleResetToGlobal}
                      className={`w-full py-2.5 rounded-xl font-medium text-sm flex items-center justify-center gap-2 transition-colors ${
                        isDark
                          ? 'bg-dark-700 text-text-secondary hover:bg-dark-600'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      <RotateCcw className="w-4 h-4" />
                      Reset to Global Settings
                    </button>
                  )}

                  {/* Background Color */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Palette className={`w-4 h-4 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                      <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        Background Color
                      </span>
                    </div>
                    <ColorPicker
                      value={slide.backgroundColor || globalTheme.colors.background}
                      onChange={handleBackgroundChange}
                      isDark={isDark}
                      themeColors={globalTheme.colors}
                    />
                    {slide.backgroundColor && (
                      <p className="text-xs text-purple-500 mt-2">
                        Custom color applied
                      </p>
                    )}
                  </div>

                  {/* Layout */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Layout className={`w-4 h-4 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                      <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        Slide Layout
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {SLIDE_LAYOUTS.slice(0, 8).map((layout) => (
                        <button
                          key={layout.id}
                          onClick={() => handleLayoutChange(layout.id as PresentationSlide['layout'])}
                          className={`p-3 rounded-xl text-left transition-all ${
                            (slide.layout || 'default') === layout.id
                              ? 'bg-purple-500 text-white'
                              : isDark
                                ? 'bg-dark-700 text-text-secondary hover:bg-dark-600'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          <p className="text-xs font-medium truncate">{layout.name}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Transition Override */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Sparkles className={`w-4 h-4 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                      <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        Transition Override
                      </span>
                      {slide.transition && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400">
                          Custom
                        </span>
                      )}
                    </div>
                    <select
                      value={slide.transition || ''}
                      onChange={(e) => handleTransitionChange(e.target.value as SlideTransition | '')}
                      className={`w-full px-3 py-2 rounded-lg border text-sm ${
                        isDark
                          ? 'bg-dark-700 border-dark-600 text-white'
                          : 'bg-white border-slate-200 text-slate-900'
                      }`}
                    >
                      <option value="">Use Global Transition</option>
                      {SLIDE_TRANSITIONS.map((t) => (
                        <option key={t.id} value={t.id}>{t.name} - {t.description}</option>
                      ))}
                    </select>
                  </div>

                  {/* Animation Override */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Sparkles className={`w-4 h-4 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                      <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        Element Animation Override
                      </span>
                      {slide.animation && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400">
                          Custom
                        </span>
                      )}
                    </div>
                    <select
                      value={slide.animation || ''}
                      onChange={(e) => handleAnimationChange(e.target.value as SlideAnimation | '')}
                      className={`w-full px-3 py-2 rounded-lg border text-sm ${
                        isDark
                          ? 'bg-dark-700 border-dark-600 text-white'
                          : 'bg-white border-slate-200 text-slate-900'
                      }`}
                    >
                      <option value="">Use Global Animation</option>
                      {ELEMENT_ANIMATIONS.map((a) => (
                        <option key={a.id} value={a.id}>{a.name} - {a.description}</option>
                      ))}
                    </select>
                  </div>

                  {/* Info Box */}
                  <div className={`p-3 rounded-xl text-xs ${
                    isDark ? 'bg-dark-700 text-text-muted' : 'bg-slate-50 text-slate-500'
                  }`}>
                    <p className="font-medium mb-1">Per-Slide Customization</p>
                    <p>
                      Changes here only affect this slide. Use global settings in the left panel for presentation-wide changes.
                    </p>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
